import { css } from '@emotion/react';
import { t } from 'i18n';
import { useState } from 'react';

import { DeviceType, useMaxDeviceWidth } from '../../hooks';
import { logMessage } from '../../utils';
import { CheckboxField } from '../forms';
import { SasIconButton } from '../SasIcon';
import { theme } from '../theme';
import { Tippy } from '../tippy/Tippy';

import { VoucherIcon } from './VoucherIcon';
import { VoucherStatus } from './VoucherStatus';
import { getVoucherInfo } from './voucherUtils';

const styles = {
  wrapper: (isActive: boolean) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: ${theme.spacing.xl};
    background-color: ${isActive ? theme.colors.background.container : theme.colors.gray90};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-radius: ${theme.borders.radius.sm};
  `,
  details: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
    justify-content: space-between;
    width: 100%;
    min-width: 0;

    @media (max-width: ${theme.breakpoints.tablet}) {
      margin-right: ${theme.spacing.xs};
      flex-direction: column;
      justify-content: start;
      gap: 0;
    }
  `,
  remark: css`
    color: ${theme.colors.title};
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.black};
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};

    @media (max-width: ${theme.breakpoints.mobileS}) {
      font-size: ${theme.fontSize.xs};
    }

    svg {
      color: var(--color-blue-2);
    }
  `,
  remarkText: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  expiryDate: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: ${theme.fontSize.sm};
    margin: 0;

    @media (max-width: ${theme.breakpoints.mobileS}) {
      font-size: ${theme.fontSize.xs};
    }
  `,
  icon: css`
    margin-left: ${theme.spacing.md};
    @media (max-width: ${theme.breakpoints.tablet}) {
      margin-left: ${theme.spacing.xs};
    }
  `,
};

type Props = {
  code: string;
  expiryDate: string;
  status: string;
  handleClick?: (fetch: boolean) => void;
};

export const Voucher = ({ code, expiryDate, status, handleClick }: Props) => {
  const isSmall = useMaxDeviceWidth(DeviceType.Tablet);
  const [displayTooltip, setDisplayTooltip] = useState(false);

  const { icon, remark, description } = getVoucherInfo(code);

  if (!icon && !remark && !description) {
    logMessage(`Missing coupon information for coupon type ${code}`);
    return null;
  }

  const isActive = status === 'active';
  const tooltipContent = !isActive ? t('coupons.description.expired') : undefined;

  return (
    <div css={styles.wrapper(isActive)}>
      {!isActive && <VoucherStatus icon="cl--alert" color={theme.colors.danger} label={t('expired')} />}
      <div css={styles.details}>
        <p css={styles.remark}>
          {remark && <span css={styles.remarkText}>{remark}</span>}
          {tooltipContent && (
            <Tippy
              placement={isSmall ? 'bottom' : 'right-start'}
              offset={isSmall ? undefined : [-8, 16]}
              content={tooltipContent}
              visible={displayTooltip}
              handleClose={() => {
                setDisplayTooltip(false);
              }}
            >
              <SasIconButton
                onClick={() => {
                  setDisplayTooltip(value => !value);
                }}
                name={'cl--info-circle'}
              />
            </Tippy>
          )}
        </p>
        {handleClick && <CheckboxField onChange={() => handleClick(true)} />}

        <p css={styles.expiryDate}>
          {isActive ? t('expires') : t('expired')} {expiryDate}
        </p>
      </div>
      <VoucherIcon css={styles.icon} type={icon} />
    </div>
  );
};
